import apiClient from './apiClient';

class BusinessUnitService {
  // Get all business units
  async getAllBusinessUnits() {
    try {
      const response = await apiClient.get('/business-units');
      console.log('BusinessUnitService - getAllBusinessUnits response:', response.data);
      
      const result = {
        success: true,
        data: Array.isArray(response.data?.data) ? response.data.data 
            : Array.isArray(response.data) ? response.data 
            : []
      };
      
      console.log('BusinessUnitService - processed result:', result);
      return result;
    } catch (error) {
      console.error('Get all business units error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch business units'
      };
    }
  }

  // Get business unit by ID
  async getBusinessUnitById(businessUnitId) {
    try {
      const response = await apiClient.get(`/business-units/${businessUnitId}`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Get business unit by ID error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch business unit'
      };
    }
  }

  // Get available options for dropdowns
  async getAvailableOptions() {
    try {
      const response = await apiClient.get('/business-units/options');
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Get available options error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch available options'
      };
    }
  }

  // Bulk import organisations + participants + groups from CSV
  async bulkImportOrganizations(formData) {
    try {
      const response = await apiClient.post('/business-units/bulk-import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000 // 5 min for large files
      });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Bulk import error:', error);
      return { success: false, message: error.response?.data?.message || 'Bulk import failed' };
    }
  }

  // Create new business unit
  async createBusinessUnit(businessUnitData) {
    try {
      const response = await apiClient.post('/business-units', businessUnitData);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Create business unit error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to create business unit'
      };
    }
  }

  // Update business unit
  async updateBusinessUnit(businessUnitId, businessUnitData) {
    try {
      const response = await apiClient.put(`/business-units/${businessUnitId}`, businessUnitData);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Update business unit error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to update business unit'
      };
    }
  }

  // Delete business unit
  async deleteBusinessUnit(businessUnitId) {
    try {
      const response = await apiClient.delete(`/business-units/${businessUnitId}`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Delete business unit error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to delete business unit'
      };
    }
  }

  // Department management
  async addDepartment(businessUnitId, departmentName) {
    try {
      const response = await apiClient.post(`/business-units/${businessUnitId}/departments`, { departmentName });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Add department error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to add department'
      };
    }
  }

  async updateDepartment(businessUnitId, oldName, newName) {
    try {
      const response = await apiClient.put(`/business-units/${businessUnitId}/departments`, { oldName, newName });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Update department error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to update department'
      };
    }
  }

  async removeDepartment(businessUnitId, departmentName) {
    try {
      const response = await apiClient.delete(`/business-units/${businessUnitId}/departments`, { 
        data: { departmentName }
      });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Remove department error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to remove department'
      };
    }
  }

  // Participant invitation methods
  
  // Send participant invitation
  async sendParticipantInvitation(businessUnitId, email) {
    try {
      console.log('=== SEND INVITATION SERVICE START ===');
      console.log('Service: Sending invitation for business unit:', businessUnitId, 'to email:', email);
      console.log('Service: Making API call to /users/participants/invite');
      
      const response = await apiClient.post('/users/participants/invite', {
        businessUnitId,
        email
      });
      
      console.log('Service: Send invitation response received:', response.data);
      console.log('=== SEND INVITATION SERVICE SUCCESS ===');
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('=== SEND INVITATION SERVICE ERROR ===');
      console.error('Send participant invitation error:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      console.error('=== SEND INVITATION SERVICE ERROR END ===');
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to send participant invitation'
      };
    }
  }

  // Check if phone number exists
  async checkPhoneNumber(phoneNumber) {
    try {
      const response = await apiClient.get('/users/check-phone', {
        params: { phoneNumber }
      });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Check phone number error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to check phone number'
      };
    }
  }

  // Get participants by business unit with search support
  async getParticipantsByBusinessUnit(businessUnitId, options = {}) {
    try {
      const url = `/users/participants/business-unit/${businessUnitId}`;
      const params = {};
      
      // Add search parameters
      if (options.department) params.department = options.department;
      if (options.search) params.search = options.search;
      if (options.page) params.page = options.page;
      if (options.limit) params.limit = options.limit;
      
      const response = await apiClient.get(url, { params });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Get participants by business unit error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch participants'
      };
    }
  }

  // Get all participants
  async getAllParticipants(businessUnit = null, department = null, page = 1, limit = 10) {
    try {
      const params = { page, limit };
      if (businessUnit) params.businessUnit = businessUnit;
      if (department) params.department = department;
      
      const response = await apiClient.get('/users/participants', { params });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Get all participants error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch participants'
      };
    }
  }

  // Resend participant invitation
  async resendParticipantInvitation(businessUnitId) {
    try {
      console.log('=== RESEND INVITATION SERVICE START ===');
      console.log('Service: Resending invitation for business unit:', businessUnitId);
      console.log('Service: Making API call to /users/participants/resend-invitation');
      
      const response = await apiClient.post('/users/participants/resend-invitation', {
        businessUnitId
      });
      
      console.log('Service: Resend invitation response received:', response.data);
      console.log('=== RESEND INVITATION SERVICE SUCCESS ===');
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('=== RESEND INVITATION SERVICE ERROR ===');
      console.error('Resend participant invitation error:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      console.error('=== RESEND INVITATION SERVICE ERROR END ===');
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to resend participant invitation'
      };
    }
  }

  // Get participant invitation details
  async getParticipantInvitationDetails(token) {
    try {
      const response = await apiClient.get(`/users/participants/invitation/${token}`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Get participant invitation details error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch invitation details'
      };
    }
  }

  // Complete participant registration
  async completeParticipantRegistration(token, participantData) {
    try {
      const response = await apiClient.post('/users/participants/register', {
        token,
        ...participantData
      });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Complete participant registration error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to complete registration'
      };
    }
  }

  // Create participant manually
  async createParticipant(participantData) {
    try {
      const response = await apiClient.post('/users/participants', participantData);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Create participant error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to create participant'
      };
    }
  }

  // Update participant
  async updateParticipant(participantId, participantData) {
    try {
      const response = await apiClient.put(`/users/participants/${participantId}`, participantData);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Update participant error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to update participant'
      };
    }
  }

  // Cancel participant invitation
  async cancelParticipantInvitation(businessUnitId) {
    try {
      console.log('=== CANCEL INVITATION SERVICE START ===');
      console.log('Service: Canceling invitation for business unit:', businessUnitId);
      console.log('Service: Making API call to DELETE /users/participants/cancel-invitation/' + businessUnitId);
      
      const response = await apiClient.delete(`/users/participants/cancel-invitation/${businessUnitId}`);
      
      console.log('Service: Cancel invitation response received:', response.data);
      console.log('=== CANCEL INVITATION SERVICE SUCCESS ===');
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('=== CANCEL INVITATION SERVICE ERROR ===');
      console.error('Cancel participant invitation error:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      console.error('=== CANCEL INVITATION SERVICE ERROR END ===');
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to cancel participant invitation'
      };
    }
  }

  // Assign business unit manager
  async assignBusinessUnitManager(businessUnitId, userId, managerGroupName) {
    try {
      console.log('Assigning business unit manager:', { businessUnitId, userId, managerGroupName });
      const response = await apiClient.post('/business-units/assign-manager', {
        businessUnitId,
        userId,
        managerGroupName
      });
      console.log('Assign manager response:', response.data);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Assign business unit manager error:', error);
      console.error('Error response:', error.response?.data);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to assign business unit manager'
      };
    }
  }
}

const businessUnitService = new BusinessUnitService();
export default businessUnitService;